<template>
  <div>
    <v-card width="400">
      <v-card-title class="text-h5 blue--text">
        Login
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="officeCode"
          label="Office"
          :prepend-icon="icon.office"
          :error-messages="errorMessages(vuelidate.officeCode)"
          @blur="officeCodeBlur"
        />
        <v-text-field
          v-model="formData.LoginName"
          label="Email"
          :prepend-icon="icon.user"
          :error-messages="errorMessages(vuelidate.formData.LoginName)"
          @blur="vuelidate.formData.LoginName.$touch()"
        />
        <v-text-field
          v-model="formData.Password"
          v-if="!forgotPasswordMode"
          :type="showPassword ? 'text' : 'password'"
          label="Password"
          :prepend-icon="icon.password"
          :append-icon="showPassword ? icon.passwordShow : icon.passwordHide"
          :error-messages="errorMessages(vuelidate.formData.Password)"
          @blur="vuelidate.formData.Password.$touch()"
          @click:append="showPassword = !showPassword"
        />
      </v-card-text>
      <v-alert v-model="showError" type="error" dismissible>
        {{ loginResultMessage }}
      </v-alert>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          v-if="!showError && !forgotPasswordMode"
          color="info"
          @click="loginClick"
          >Login
        </v-btn>
        <v-btn
          v-if="forgotPasswordMode"
          color="info"
          @click="emailTempPasswordClick"
          >Email Temporary Password</v-btn
        >
        <v-spacer />
        <v-btn
          v-if="!forgotPasswordMode"
          @click="forgotPasswordClick"
          small
          color="error"
          >Forgot Password
        </v-btn>
      </v-card-actions>
    </v-card>
    <DialogUtil ref="dialogUtil" />
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { icon } from '@/use/Constants'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email } from '@vuelidate/validators'
import { login, logout, userData } from '@/store/UserSession'
import { host } from '@/services/HostAPI'
import FieldService from '@/services/FieldService'
import { cloudManager } from '@/services/CloudManagerAPI'

export default {
  name: 'Login',
  props: {
    defaultOfficeCode: {
      type: String,
      default: null
    },
    isDialogOpen: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    watch(
      () => props.isDialogOpen,
      newVal => {
        newVal && initDialog()
      }
    )

    const dialogUtil = ref(null)
    const showPassword = ref(false)
    const showError = ref(false)
    const loginResultMessage = ref()
    const selectedOfficeInfo = ref()
    const forgotPasswordMode = ref(false)

    const officeCode = ref()
    const formData = ref({
      LoginName: null,
      Password: null
    })

    const initDialog = () => {
      if (props.defaultOfficeCode) {
        officeCode.value = props.defaultOfficeCode
        getOfficeInfo()
      } else if (userData.value.OfficeCode) {
        officeCode.value = userData.value.OfficeCode
        getOfficeInfo()
      }

      formData.value.LoginName = userData.value.LoginName
    }

    const officeCodeBlur = () => {
      if (officeCode.value) {
        getOfficeInfo()
      }
    }

    watch(
      () => selectedOfficeInfo.value,
      newVal => {
        if (newVal) {
          // Banner monitors this -- will show when populated
          FieldService.setTenantId(newVal.SubDomain)
          userData.value.OfficeName = newVal.OfficeName
          userData.value.OfficeId = newVal.OfficeId
        }
      }
    )

    const getOfficeInfo = async () => {
      const rq = {
        Code: officeCode.value
      }
      const rs = await cloudManager.office.retrieve(rq)
      if (rs.IsSuccess) {
        FieldService.setTenantId(rs.Info.SubDomain)
        selectedOfficeInfo.value = rs.Info
      } else {
        logout()
        selectedOfficeInfo.value = {
          OfficeId: null,
          OfficeName: null
        }
      }
    }

    const rules = {
      officeCode: {
        required,
        customRule: {
          $validator: () => {
            return selectedOfficeInfo.value && selectedOfficeInfo.value.OfficeId
          },
          $message: 'Invalid office code'
        }
      },
      formData: {
        LoginName: { required, email },
        Password: {
          required: requiredIf(() => {
            return !forgotPasswordMode.value
          })
        }
      }
    }

    const vuelidate = useVuelidate(rules, { officeCode, formData })

    const errorMessages = item => {
      return vuelidate.value.$invalid ? item.$errors.map(e => e.$message) : []
    }

    const loginClick = async () => {
      vuelidate.value.$touch()
      if (vuelidate.value.$invalid) {
        return
      }

      const loginResult = await login(officeCode.value, formData.value)
      if (loginResult.IsSuccess) {
        context.emit('loggedIn')
      } else {
        loginResultMessage.value = loginResult.Message
        showError.value = true
      }
    }

    const forgotPasswordClick = () => {
      forgotPasswordMode.value = true
    }

    const emailTempPasswordClick = async () => {
      vuelidate.value.$touch()
      if (vuelidate.value.$invalid) {
        return
      }

      const rq = {
        LoginSource: 'C',
        OfficeId: userData.value.OfficeId,
        Email: formData.value.LoginName
      }

      const rs = await host.customerPortal.forgotPassword(rq)
      if (rs.IsSuccess) {
        dialogUtil.value.inform(rs.Message)
        formData.value.Password = null
        forgotPasswordMode.value = false
      } else {
        dialogUtil.value.error(rs.Message)
      }
    }

    initDialog()

    return {
      userData,
      dialogUtil,
      officeCode,
      formData,
      showPassword,
      loginClick,
      icon,
      forgotPasswordMode,
      forgotPasswordClick,
      emailTempPasswordClick,
      showError,
      loginResultMessage,
      errorMessages,
      vuelidate,
      officeCodeBlur
    }
  }
}
</script>
